import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Globe, Loader2, RotateCcw } from "lucide-react";

const CURRENCIES = [
  { code: "INR", symbol: "₹", rate: 1 },
  { code: "AED", symbol: "AED ", rate: 0.044 },
  { code: "USD", symbol: "$", rate: 0.012 },
  { code: "THB", symbol: "฿", rate: 0.43 },
  { code: "VND", symbol: "₫", rate: 300 },
];

const fmt = (v, c) => {
  const n = Math.round(v * c.rate);
  return c.code === "VND" ? `${c.symbol}${n.toLocaleString("en-US")}` : `${c.symbol}${n.toLocaleString("en-IN")}`;
};

export default function SimPricingPublish() {
  const [cost, setCost] = useState(72000);
  const [markup, setMarkup] = useState(20);
  const [cur, setCur] = useState(CURRENCIES[0]);
  const [stage, setStage] = useState("idle"); // idle | publishing | published
  const selling = cost * (1 + markup / 100);

  const publish = () => {
    setStage("publishing");
    setTimeout(() => setStage("published"), 1600);
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="sim-pricing-publish">
      <div className="rounded-2xl border border-sand bg-white p-5 sm:p-6">
        <p className="mb-4 flex items-center justify-between font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-stone2">
          Package pricing · you set the numbers
          <span className="rounded-full bg-parch px-2 py-0.5 text-[9px] normal-case tracking-normal">sample data</span>
        </p>
        <label className="mb-1 block text-xs font-semibold text-ink">Supplier cost</label>
        <input
          type="number" value={cost} min={0}
          onChange={(e) => setCost(Number(e.target.value) || 0)}
          data-testid="sim-pricing-cost-input"
          className="mb-4 w-full rounded-lg border border-sand px-3.5 py-2.5 text-sm outline-none focus:border-terra"
        />
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-semibold text-ink">Markup — fixed or percentage</label>
          <span className="font-mono text-xs font-bold text-terra" data-testid="sim-pricing-markup-value">{markup}%</span>
        </div>
        <input
          type="range" min={0} max={60} value={markup}
          onChange={(e) => setMarkup(Number(e.target.value))}
          data-testid="sim-pricing-markup-slider"
          className="mb-4 w-full accent-[#C49A5A]"
        />
        <label className="mb-1 block text-xs font-semibold text-ink">Display currency</label>
        <div className="mb-5 flex flex-wrap gap-1.5">
          {CURRENCIES.map((c) => (
            <button
              key={c.code}
              onClick={() => setCur(c)}
              data-testid={`sim-pricing-currency-${c.code.toLowerCase()}`}
              className={`rounded-full px-3 py-1.5 font-mono text-[11px] font-bold transition-colors duration-300 ${cur.code === c.code ? "bg-ink text-paper" : "bg-parch text-stone2 hover:text-ink"}`}
            >
              {c.code}
            </button>
          ))}
        </div>
        <div className="flex items-end justify-between rounded-xl bg-ink p-4 text-paper">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-paper/50">Selling price</p>
            <p className="font-serif text-3xl" data-testid="sim-pricing-selling-price">{fmt(selling, cur)}</p>
            <p className="mt-1 text-[11px] text-paper/50">per package · adult, child, occupancy and seasonal rates supported</p>
          </div>
          <p className="text-right font-mono text-[10px] text-paper/40">cost {fmt(cost, cur)}<br />+{markup}% markup</p>
        </div>
      </div>

      <div className="flex flex-col rounded-2xl border border-sand bg-white p-5 sm:p-6">
        <p className="mb-4 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-stone2">Publish</p>
        <AnimatePresence mode="wait">
          {stage !== "published" ? (
            <motion.div key="pre" exit={{ opacity: 0, y: -10 }} className="flex flex-1 flex-col items-center justify-center py-8 text-center">
              <p className="max-w-xs text-sm leading-relaxed text-stone2">
                Maldives Escape, priced at <span className="font-bold text-ink">{fmt(selling, cur)}</span>.
                One click sends it live on your website.
              </p>
              <button
                onClick={publish}
                disabled={stage === "publishing"}
                data-testid="sim-publish-btn"
                className="mt-6 inline-flex min-w-[220px] items-center justify-center gap-2 rounded-full bg-terra px-8 py-4 text-sm font-bold text-ink transition-colors duration-300 hover:bg-ink hover:text-paper disabled:opacity-80"
              >
                {stage === "publishing" ? (<><Loader2 className="h-4 w-4 animate-spin" /> Publishing…</>) : (<><Globe className="h-4 w-4" /> Publish Package</>)}
              </button>
              {stage === "publishing" && (
                <div className="mt-4 h-1 w-56 overflow-hidden rounded-full bg-parch">
                  <div className="shimmer h-full w-1/3 rounded-full bg-terra" />
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key="post" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex-1" data-testid="sim-publish-success">
              <div className="flex items-center gap-2 rounded-xl bg-emerald2/10 px-4 py-3 text-sm font-bold text-emerald2">
                <Check className="h-5 w-5" /> PACKAGE PUBLISHED
              </div>
              <p className="mt-3 font-mono text-[11px] text-stone2">
                Live at: <span className="font-bold text-ink">yourdmc.com/packages/maldives-escape</span>
              </p>
              <div className="mt-4 overflow-hidden rounded-xl border border-sand">
                <div className="relative h-28">
                  <img src="https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=900&q=70" alt="Maldives Escape" loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute left-3 top-3 rounded-full bg-emerald2 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-paper">Live</span>
                </div>
                <div className="flex items-center justify-between p-3.5">
                  <div>
                    <p className="text-sm font-bold text-ink">Maldives Escape</p>
                    <p className="text-[11px] text-stone2">6 Nights / 7 Days</p>
                  </div>
                  <p className="font-serif text-lg text-ink">{fmt(selling, cur)}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <p className="text-xs font-semibold text-stone2">No developer required.</p>
                <button onClick={() => setStage("idle")} data-testid="sim-publish-reset-btn" className="inline-flex items-center gap-1.5 text-xs font-bold text-terra hover:text-ink">
                  <RotateCcw className="h-3.5 w-3.5" /> Replay
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const days = () => "";
